import Dialog from '@mui/material/Dialog';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'store/modules/base/redux';

/**
 * Implements a DialogContainer responsible for showing all dialogs. Necessary
 * for supporting @atlaskit's modal animations.
 *
 */
class DialogContainer extends React.PureComponent {
  /**
   * Returns the dialog to be displayed.
   *
   * @private
   * @returns {ReactElement|null}
   */
  _renderDialogContent() {
    const { _component: component, _reducedUI: reducedUI } = this.props;

    return component && !reducedUI
      ? React.createElement(component, this.props._componentProps)
      : null;
  }

  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
  render() {
    return (
      <Dialog
        style={{ backgroundColor: 'transparent' }}
        slotProps={{
          backdrop: { sx: { background: 'transparent' } }
        }}
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none'
          }
        }}
        BackdropProps={{ invisible: true }}
        fullScreen
        open={this.props._component !== undefined}>
        {this._renderDialogContent()}
      </Dialog>
    );
  }
}

/**
 * Maps (parts of) the redux state to the associated
 * {@code AbstractDialogContainer}'s props.
 *
 * @param {Object} state - The redux state.
 * @private
 * @returns {IProps}
 */
function mapStateToProps(state) {
  const stateFeaturesBaseDialog = state['features/base/dialog'];
  const { reducedUI } = state['features/base/responsive-ui'];

  return {
    _component: stateFeaturesBaseDialog.component,
    _componentProps: stateFeaturesBaseDialog.componentProps,
    _isNewDialog: stateFeaturesBaseDialog.isNewDialog,
    _reducedUI: reducedUI
  };
}

export default connect(mapStateToProps)(withBoundary(DialogContainer));
DialogContainer.propTypes = {
  /**
   * The component to render.
   */
  _component: PropTypes.any,

  /**
   * The props to pass to the component that will be rendered.
   */
  _componentProps: PropTypes.object,

  /**
   * Whether the dialog is using the new component.
   */
  _isNewDialog: PropTypes.bool,

  /**
   * Array of reactions to be displayed.
   */
  _reactionsQueue: PropTypes.array,

  /**
   * True if the UI is in a compact state where we don't show dialogs.
   */
  _reducedUI: PropTypes.bool
};
